'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiClient } from '@/lib/api/client'
import type { CustomerType } from '@/lib/api/types'

export function NewCustomerForm() {
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [type, setType] = useState<CustomerType>('INDIVIDUAL')

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    const formData = new FormData(e.currentTarget)
    const companyName = type === 'BUSINESS' ? String(formData.get('company_name') || '').trim() : ''

    try {
      const customer = await apiClient.createCustomer({
        name: String(formData.get('name') || '').trim(),
        type,
        company_name: type === 'BUSINESS' ? companyName : null,
        contact_person: type === 'BUSINESS' ? String(formData.get('contact_person') || '').trim() || null : null,
        tin_number: String(formData.get('tin_number') || '').trim(),
        phone: String(formData.get('phone') || '').trim(),
        email: String(formData.get('email') || '').trim(),
      })

      router.push(`/customers/${customer.id}`)
      router.refresh()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create customer')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="type">Customer Type</Label>
        <Select value={type} onValueChange={(value) => setType(value as CustomerType)}>
          <SelectTrigger id="type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="INDIVIDUAL">Individual</SelectItem>
            <SelectItem value="BUSINESS">Business</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">
          {type === 'BUSINESS' ? 'Contact Name' : 'Full Name'} *
        </Label>
        <Input
          id="name"
          name="name"
          required
          placeholder={type === 'BUSINESS' ? 'Jonathan Bakebwa' : 'Enter full name'}
        />
      </div>

      {type === 'BUSINESS' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="company_name">Company Name *</Label>
            <Input
              id="company_name"
              name="company_name"
              required
              placeholder="XTELLAR SYSTEMS CO. LIMITED"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_person">Contact Person</Label>
            <Input
              id="contact_person"
              name="contact_person"
              placeholder="Optional contact person"
            />
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          name="email"
          type="email"
          required
          placeholder="customer@example.com"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone *</Label>
        <Input
          id="phone"
          name="phone"
          required
          placeholder="+256 700 000 000"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tin_number">TIN Number *</Label>
        <Input
          id="tin_number"
          name="tin_number"
          required
          placeholder="1000000000"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Create Customer
        </Button>
        <Link href="/customers">
          <Button type="button" variant="outline">
            Cancel
          </Button>
        </Link>
      </div>
    </form>
  )
}
